/**
 * Read-only preview table for the HR leave-balance import dry-run.
 *
 * Renders each LeaveRowResult as a row: status badge, employee number,
 * matched user (name + email, with a "name differs" warning when
 * nameMismatch), leave type, entitlement/used/CSV-balance columns, the
 * LMS-computed available with a ✓ (matches) or ⚠ (discrepancy, reason in
 * tooltip), and a notes/error column.
 */

import type { LeaveRowResult } from '../../api/hr-leave-import'

interface Props {
  rows: LeaveRowResult[]
  /** Row indices HR has chosen to exclude from the import. */
  skipped: ReadonlySet<number>
  onToggleSkip: (rowIndex: number) => void
}

const ACTION_LABEL: Record<LeaveRowResult['action'], string> = {
  create: 'New',
  update: 'Update',
  skip: 'Skipped',
  error: 'Error',
}

// ── Badge styling — mirrors the inline-style badge pattern from PreviewGrid ──

function actionBadge(action: LeaveRowResult['action']): React.CSSProperties {
  switch (action) {
    case 'create':
      return {
        background: 'var(--success-bg)',
        color: 'var(--success-fg)',
      }
    case 'update':
      return {
        background: 'var(--primary-bg)',
        color: 'var(--steel-blue)',
      }
    case 'skip':
      return {
        background: 'var(--surface-2)',
        color: 'var(--text-3)',
      }
    case 'error':
      return {
        background: 'var(--danger-bg)',
        color: 'var(--danger-fg)',
      }
  }
}

// ── Component ────────────────────────────────────────────────────────────────

export function LeaveImportPreviewTable({ rows, skipped, onToggleSkip }: Props): JSX.Element {
  return (
    <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 8 }}>
      <table
        aria-label="Import preview"
        style={{ borderCollapse: 'collapse', fontSize: 12, minWidth: '100%' }}
      >
        <thead>
          <tr style={{ background: 'var(--surface-2)' }}>
            <HeaderCell>Skip</HeaderCell>
            <HeaderCell>Status</HeaderCell>
            <HeaderCell>Employee No.</HeaderCell>
            <HeaderCell>Matched user</HeaderCell>
            <HeaderCell>Leave type</HeaderCell>
            <HeaderCell>Entitlement</HeaderCell>
            <HeaderCell>Used</HeaderCell>
            <HeaderCell>CSV balance</HeaderCell>
            <HeaderCell>New system shows</HeaderCell>
            <HeaderCell>Notes</HeaderCell>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isSkipped = skipped.has(r.rowIndex)
            return (
            <tr
              key={String(r.rowIndex)}
              data-skipped={isSkipped ? 'true' : 'false'}
              style={{
                background: isSkipped
                  ? 'var(--surface-2)'
                  : r.action === 'error'
                    ? 'var(--danger-bg)'
                    : undefined,
                opacity: isSkipped ? 0.5 : 1,
                textDecoration: isSkipped ? 'line-through' : 'none',
                transition: 'opacity 120ms ease, background 120ms ease',
              }}
            >
              {/* Skip toggle */}
              <td style={cellStyle}>
                <button
                  type="button"
                  data-testid="skip-toggle"
                  aria-pressed={isSkipped}
                  onClick={() => onToggleSkip(r.rowIndex)}
                  style={{
                    padding: '2px 8px',
                    borderRadius: 4,
                    border: '1px solid var(--border)',
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                    background: isSkipped ? 'var(--steel-blue)' : 'var(--surface)',
                    color: isSkipped ? 'var(--surface)' : 'var(--text-2)',
                  }}
                >
                  {isSkipped ? 'Skipped' : 'Skip'}
                </button>
              </td>

              {/* Status badge */}
              <td style={cellStyle}>
                <span
                  style={{
                    ...actionBadge(r.action),
                    padding: '2px 8px',
                    borderRadius: 4,
                    fontSize: 11,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {ACTION_LABEL[r.action]}
                </span>
              </td>

              {/* Employee number */}
              <td style={{ ...cellStyle, fontFamily: 'monospace' }}>{r.employeeNo}</td>

              {/* Matched user — name + email + optional name-mismatch warning */}
              <td style={cellStyle}>
                {r.matchedEmployeeEmail != null ? (
                  <span>
                    <span style={{ fontWeight: 500 }}>{r.matchedEmployeeName}</span>
                    <br />
                    <span style={{ color: 'var(--text-3)' }}>{r.matchedEmployeeEmail}</span>
                    {r.nameMismatch ? (
                      <span
                        title={`CSV said: ${r.csvEmployeeName}`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 3,
                          marginLeft: 6,
                          padding: '1px 6px',
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 600,
                          background: 'var(--warning-bg, var(--danger-bg))',
                          color: 'var(--warning-fg, var(--danger-fg))',
                          cursor: 'help',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        ⚠ name differs
                      </span>
                    ) : null}
                  </span>
                ) : (
                  <span style={{ color: 'var(--text-3)' }}>
                    {r.csvEmployeeName.trim().length > 0
                      ? `no match for ${r.csvEmployeeName.trim()}`
                      : 'no match'}
                  </span>
                )}
              </td>

              {/* Leave type */}
              <td style={cellStyle}>{r.leaveType ?? '—'}</td>

              {/* Entitlement */}
              <td style={{ ...cellStyle, textAlign: 'right' }}>{r.entitlement ?? '—'}</td>

              {/* Used */}
              <td style={{ ...cellStyle, textAlign: 'right' }}>{r.used ?? '—'}</td>

              {/* CSV balance */}
              <td style={{ ...cellStyle, textAlign: 'right' }}>{r.csvBalance ?? '—'}</td>

              {/* LMS-computed available + match/discrepancy indicator */}
              <td style={{ ...cellStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                {r.lmsComputedAvailable ?? '—'}
                {r.discrepancy ? (
                  <span
                    title={r.discrepancyReason ?? ''}
                    aria-label="discrepancy"
                    style={{ marginLeft: 4, color: 'var(--danger-fg)', cursor: 'help' }}
                  >
                    ⚠
                  </span>
                ) : r.action === 'create' || r.action === 'update' ? (
                  <span
                    aria-label="matches"
                    style={{ marginLeft: 4, color: 'var(--success-fg)' }}
                  >
                    ✓
                  </span>
                ) : null}
              </td>

              {/* Notes / error */}
              <td
                style={{
                  ...cellStyle,
                  color:
                    r.action === 'error'
                      ? 'var(--danger-fg)'
                      : 'var(--warning-fg, var(--text-3))',
                  minWidth: 140,
                }}
              >
                {r.error ?? r.discrepancyReason ?? r.note ?? ''}
              </td>
            </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ── Shared cell style ────────────────────────────────────────────────────────

const cellStyle: React.CSSProperties = {
  padding: '4px 8px',
  borderBottom: '1px solid var(--border)',
  verticalAlign: 'middle',
}

// ── Header cell helper — matches PreviewGrid's HeaderCell ────────────────────

function HeaderCell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <th
      scope="col"
      style={{
        padding: '8px 10px',
        textAlign: 'left',
        fontWeight: 600,
        color: 'var(--text-3)',
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        borderBottom: '1px solid var(--border)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </th>
  )
}
